import React from 'react';
import { Card, Heading, Text } from '@radix-ui/themes';
import MaterialIcon from '../common/MaterialIcon';

type PastWinner = {
  memberName: string;
  memberImageUrl: string;
  afterYear: number;
  previousYear: number;
  beforeWeight: number;
  afterWeight: number;
  difference: number;
};

type PastWinnersTabProps = {
  pastWinners: PastWinner[];
};

function PastWinnersTab({ pastWinners }: PastWinnersTabProps) {
  return (
    <>
      <Heading size="6" className="font-display text-pine-900">
        <span className="inline-flex items-center gap-2">
          <MaterialIcon name="history_edu" className="text-[24px] text-pine-700" />
          Tidligere vindere
        </span>
      </Heading>

      <Text size="2" color="gray" className="mt-2 block">
        Historiske vindere beregnet på samme måde som "Årets Fede Svin" på forsiden.
      </Text>

      {!pastWinners.length ? (
        <Text size="2" color="gray" className="mt-4 block rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
          Ingen tidligere vindere endnu. Der skal være data for både "Før Julefrokost" året før og
          "Efter Julefrokost" i det valgte år.
        </Text>
      ) : (
        <div className="mt-4 grid gap-4">
          {pastWinners.map((winner) => {
            const isSameYearEveningWinner = winner.previousYear === winner.afterYear;

            return (
              <Card key={`${winner.afterYear}-${winner.memberName}`} className="rounded-2xl border border-pine-100 bg-white/80 p-4 shadow-sm">
                <div className="grid gap-4 sm:grid-cols-[112px_1fr] sm:items-center">
                  <div className="h-28 w-28 overflow-hidden rounded-2xl border border-pine-100 bg-slate-100">
                    {winner.memberImageUrl ? (
                      <img
                        src={winner.memberImageUrl}
                        alt={winner.memberName}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </div>

                  <div>
                    <Text size="5" weight="bold" className="uppercase tracking-[0.22em] text-pine-600">
                      <span className="inline-flex items-center gap-1.5">
                        <MaterialIcon name="emoji_events" className="text-[20px]" />
                        Vinder {winner.afterYear}
                      </span>
                    </Text>

                    <Heading size="6" className="mt-2 font-display text-pine-900">
                      {winner.memberName}
                    </Heading>

                    <Text size="2" className="mt-2 block text-slate-700">
                      {winner.afterYear} efter Julefrokost: {winner.afterWeight.toFixed(1)} kg · {winner.previousYear}
                      {' '}
                      før Julefrokost: {winner.beforeWeight.toFixed(1)} kg
                    </Text>

                    <Text size="2" weight="medium" className="mt-2 block text-pine-800">
                      <span className="inline-flex items-center gap-1">
                        <MaterialIcon name={winner.difference >= 0 ? 'trending_up' : 'trending_down'} className="text-[18px]" />
                        {winner.difference >= 0 ? '+' : ''}
                        {winner.difference.toFixed(1)} kg {isSameYearEveningWinner ? 'på aftenen' : 'mellem årene'}
                      </span>
                    </Text>

                    {isSameYearEveningWinner && (
                      <Text size="1" className="mt-2 block text-amber-700">
                        <span className="inline-flex items-center gap-1">
                          <MaterialIcon name="info" className="text-[15px]" />
                          2022 var første år, så vinderen er den med størst vægtstigning på selve aftenen.
                        </span>
                      </Text>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}

export default PastWinnersTab;
